import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/features/help/utils/logger';
import { validateFileTypes, convertExtensionsToMimeTypes } from '../utils/validation.utils';
import { FileData } from '../types/forms';

interface UploadingFile extends FileData {
  progress: number;
  status: 'uploading' | 'complete' | 'error';
}

interface UseFileUploadProps {
  acceptedTypes?: string;
  onChange: (files: FileData[]) => void;
  existingFiles?: FileData[];
}

interface UseFileUploadReturn {
  uploadingFiles: UploadingFile[];
  isDragging: boolean;
  fileTypeError: string | null;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent) => void;
  displayTypes: string;
}

// Purpose: Custom hook to manage file upload state and logic
export const useFileUpload = ({
  acceptedTypes = 'application/pdf',
  onChange,
  existingFiles = [],
}: UseFileUploadProps): UseFileUploadReturn => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);

  // Purpose: Get display types for UI
  const getDisplayTypes = useCallback((acceptedTypes: string): string => {
    // For now, simplified for PDF only - this could be expanded
    if (acceptedTypes.includes('.pdf') || acceptedTypes.includes('application/pdf')) {
      return 'PDF';
    }
    return 'supported files';
  }, []);

  // Initialize with existing files if provided
  useEffect(() => {
    // If existingFiles is undefined, null, or empty, reset uploadingFiles
    if (!existingFiles || existingFiles.length === 0) {
      setUploadingFiles((currentUploadingFiles) => {
        // Use functional update to access current state without stale closure
        if (currentUploadingFiles.length > 0) {
          const allFilesAreCompleteOrError = currentUploadingFiles.every(
            (f) => f.status === 'complete' || f.status === 'error'
          );
          if (allFilesAreCompleteOrError) {
            return []; // Clear the files
          }
        }
        return currentUploadingFiles; // No change needed
      });
      return;
    }

    // We have existing files. Map them to UploadingFile type.
    const validExistingFiles = existingFiles.filter((file) => file && file.name);

    if (validExistingFiles.length > 0) {
      const existingUploadingFiles: UploadingFile[] = validExistingFiles.map((file) => ({
        ...file,
        file: file.file || undefined,
        progress: 100,
        status: 'complete' as const,
      }));

      setUploadingFiles((currentUploadingFiles) => {
        // Use functional update to compare with current state (no stale closure!)
        if (JSON.stringify(currentUploadingFiles) !== JSON.stringify(existingUploadingFiles)) {
          return existingUploadingFiles;
        }
        return currentUploadingFiles; // No change needed
      });
    } else {
      setUploadingFiles((currentUploadingFiles) => {
        if (currentUploadingFiles.length > 0) {
          return [];
        }
        return currentUploadingFiles; // No change needed
      });
    }
  }, [existingFiles]);

  // Purpose: Handle file selection and validation
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      setFileTypeError(null);

      // Convert extensions to MIME types and validate
      const convertedMimeTypes = convertExtensionsToMimeTypes(acceptedTypes);
      const { validFiles, invalidFiles } = validateFileTypes(files, convertedMimeTypes);

      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map((f) => f.name || 'Unnamed File');
        const displayTypes = getDisplayTypes(acceptedTypes);
        const errorMsg = `Invalid file type(s): ${invalidFileNames.join(', ')}. Only ${displayTypes} files are accepted.`;
        setFileTypeError(errorMsg);
        logger.warn('useFileUpload', 'Invalid file types detected', {
          invalidCount: invalidFiles.length,
          invalidNames: invalidFileNames,
          acceptedTypes: convertedMimeTypes,
        });
      }

      if (validFiles.length === 0) {
        event.target.value = '';
        return;
      }

      // Create initial uploading files ONLY for valid files
      const newUploadingFiles: UploadingFile[] = validFiles.map((file) => ({
        file,
        name: file.name,
        contentType: file.type,
        progress: 0,
        status: 'uploading',
      }));

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      try {
        // Simulate upload progress for each valid file
        await Promise.all(
          validFiles.map(async (file) => {
            for (let progress = 0; progress <= 100; progress += 10) {
              await new Promise((resolve) => setTimeout(resolve, 200));
              setUploadingFiles((prev) =>
                prev.map((f) =>
                  f.name === file.name && f.status === 'uploading' ? { ...f, progress } : f
                )
              );
            }
          })
        );

        // Mark valid files as complete
        setUploadingFiles((prev) =>
          prev.map((f) =>
            validFiles.some((vf) => vf.name === f.name && f.status === 'uploading')
              ? { ...f, status: 'complete' as const }
              : f
          )
        );

        // Get all complete files and notify parent
        setUploadingFiles((currentUploadingFiles) => {
          const allCompleteFiles = currentUploadingFiles
            .filter((f) => f.status === 'complete')
            .map(({ file, name, contentType }) => ({ file, name, contentType }));

          onChange(allCompleteFiles);

          logger.info('useFileUpload', 'Files processed successfully', {
            totalFiles: allCompleteFiles.length,
            processedCount: validFiles.length,
            fileData: allCompleteFiles.map((f) => ({ name: f.name, type: f.contentType })),
          });

          return currentUploadingFiles;
        });
      } catch (error) {
        // Mark affected valid files as error
        setUploadingFiles((prev) =>
          prev.map((f) =>
            validFiles.some((vf) => vf.name === f.name && f.status === 'uploading')
              ? { ...f, status: 'error' as const }
              : f
          )
        );

        logger.error('useFileUpload', 'Error processing files', {
          error,
          fileCount: validFiles.length,
        });
      }

      event.target.value = '';
    },
    [acceptedTypes, onChange]
  );

  // Purpose: Handle file removal
  const handleRemoveFile = useCallback(
    (index: number) => {
      setUploadingFiles((prev) => {
        const newFiles = prev.filter((_, i) => i !== index);
        const completeFiles = newFiles
          .filter((f) => f.status === 'complete')
          .map(({ file, name, contentType }) => ({ file, name, contentType }));
        onChange(completeFiles);

        if (newFiles.length === 0) {
          setFileTypeError(null);
        }

        return newFiles;
      });
    },
    [onChange]
  );

  // Purpose: Handle drag over events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Purpose: Handle drag leave events
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Purpose: Handle drop events
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        const syntheticEvent = {
          target: {
            files,
            value: '',
          },
          currentTarget: {
            files,
            value: '',
          },
          preventDefault: () => {},
          stopPropagation: () => {},
          nativeEvent: e.nativeEvent,
          bubbles: true,
          cancelable: true,
          timeStamp: e.timeStamp,
          type: 'change',
          isTrusted: true,
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleFileChange(syntheticEvent);
      }
    },
    [handleFileChange]
  );

  return {
    uploadingFiles,
    isDragging,
    fileTypeError,
    handleFileChange,
    handleRemoveFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    displayTypes: getDisplayTypes(acceptedTypes),
  };
};
